export type WorkflowKind = 'monitor' | 'report' | 'reminder' | 'sync'
export type WorkflowActivationMode = 'auto_run' | 'manual_review'

export interface WorkflowSpec {
  kind: WorkflowKind
  objective?: string
  triggerLabel?: string
  sourceSummary?: string
  cadenceSummary?: string
  checks?: string[]
  alertConditions?: string[]
  destinations?: string[]
  rollup?: string
  activationMode?: WorkflowActivationMode
}

export interface WorkflowReadiness {
  isReady: boolean
  score: number
  blockers: string[]
  warnings: string[]
}

export interface RecurringTaskProposal {
  title: string
  description: string
  category: string
  color?: string
  frequency: 'daily' | 'weekly' | 'monthly' | 'custom'
  customDays?: number[]
  dayOfWeek?: number
  dayOfMonth?: number
  timeOfDay: string
  connectorId?: string
  connectorName?: string
  agentPrompt: string
  endsAt?: string
  workflowSpec?: WorkflowSpec
}

export const WEEKDAYS_NO = ['mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag', 'søndag']
export const WEEKDAY_LABELS_SHORT = ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn']

export function formatRecurringFrequency(task: Pick<RecurringTaskProposal, 'frequency' | 'dayOfWeek' | 'dayOfMonth' | 'customDays'>): string {
  if (task.frequency === 'daily') return 'Daglig'
  if (task.frequency === 'weekly' && task.dayOfWeek !== undefined) {
    return `Hver ${WEEKDAYS_NO[task.dayOfWeek]}`
  }
  if (task.frequency === 'monthly' && task.dayOfMonth !== undefined) {
    return `Den ${task.dayOfMonth}. hver måned`
  }
  if (task.frequency === 'custom' && task.customDays?.length) {
    return task.customDays.map(day => WEEKDAY_LABELS_SHORT[day]).join(', ')
  }
  return task.frequency
}

export function buildCadenceSummary(task: Pick<RecurringTaskProposal, 'frequency' | 'dayOfWeek' | 'dayOfMonth' | 'customDays' | 'timeOfDay'>): string {
  return `${formatRecurringFrequency(task)} kl. ${task.timeOfDay} (norsk tid)`
}

export function normalizeWorkflowSpec(
  proposal: Pick<RecurringTaskProposal, 'description' | 'connectorName' | 'frequency' | 'dayOfWeek' | 'dayOfMonth' | 'customDays' | 'timeOfDay'>,
  workflowSpec?: WorkflowSpec,
): WorkflowSpec | undefined {
  if (!workflowSpec?.kind) return workflowSpec

  const cadenceSummary = workflowSpec.cadenceSummary || buildCadenceSummary(proposal)
  return {
    ...workflowSpec,
    objective: workflowSpec.objective || proposal.description || undefined,
    triggerLabel: workflowSpec.triggerLabel || cadenceSummary,
    sourceSummary: workflowSpec.sourceSummary || (proposal.connectorName ? `Kjører mot ${proposal.connectorName}` : undefined),
    cadenceSummary,
    activationMode: workflowSpec.activationMode || 'auto_run',
  }
}

export function workflowKindLabel(kind?: WorkflowKind): string {
  if (kind === 'monitor') return 'Monitor'
  if (kind === 'report') return 'Rapport'
  if (kind === 'reminder') return 'Briefing'
  if (kind === 'sync') return 'Sync'
  return 'Workflow'
}

export function workflowKindAccent(kind?: WorkflowKind): { text: string; border: string; background: string } {
  if (kind === 'monitor') {
    return { text: '#FCD34D', border: 'rgba(245,158,11,0.22)', background: 'rgba(245,158,11,0.1)' }
  }
  if (kind === 'report') {
    return { text: '#A7F3D0', border: 'rgba(16,185,129,0.22)', background: 'rgba(16,185,129,0.1)' }
  }
  if (kind === 'reminder') {
    return { text: '#C4B5FD', border: 'rgba(139,92,246,0.22)', background: 'rgba(139,92,246,0.1)' }
  }
  if (kind === 'sync') {
    return { text: '#93C5FD', border: 'rgba(59,130,246,0.22)', background: 'rgba(59,130,246,0.1)' }
  }
  return { text: '#D1D5DB', border: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)' }
}

export function workflowActivationCopy(mode?: WorkflowActivationMode): string {
  if (mode === 'manual_review') return 'Krever manuell vurdering før neste steg'
  return 'Kjører som aktiv workflow'
}

export function workflowActivationLabel(mode?: WorkflowActivationMode): string {
  if (mode === 'manual_review') return 'Manuell review'
  return 'Auto-run'
}

export function workflowReadiness(proposal: Pick<RecurringTaskProposal, 'title' | 'description' | 'agentPrompt' | 'connectorId' | 'connectorName' | 'workflowSpec'>): WorkflowReadiness {
  const blockers: string[] = []
  const warnings: string[] = []
  const spec = proposal.workflowSpec

  if (!proposal.title.trim()) blockers.push('Workflowen mangler navn.')
  if (!proposal.agentPrompt.trim()) blockers.push('Workflowen mangler agent-instruksjon.')
  if (!spec?.kind) blockers.push('Workflow-type er ikke satt.')
  if (proposal.connectorId && !proposal.connectorName) warnings.push('Workflowen refererer til en tilkobling som ikke er navngitt.')

  if (spec) {
    if (!spec.objective?.trim()) warnings.push('Målet er uklart formulert.')
    if (!spec.triggerLabel?.trim()) warnings.push('Trigger-beskrivelsen er ikke tydelig.')
    if (!spec.checks?.length) {
      if (spec.kind === 'monitor') blockers.push('Monitoren mangler checks.')
      else warnings.push('Workflowen mangler tydelige checks.')
    }
    if (!spec.destinations?.length) {
      if (spec.kind === 'report' || spec.kind === 'reminder') blockers.push('Workflowen mangler leveransekanal.')
      else warnings.push('Leveransekanal er ikke spesifisert.')
    }
    if (!spec.rollup?.trim()) warnings.push('Rollup/oppsummering er ikke definert.')
    if (spec.kind === 'monitor' && !spec.alertConditions?.length) blockers.push('Monitoren mangler alert conditions.')
    if (spec.kind === 'report' && !spec.rollup?.trim()) blockers.push('Rapport-workflowen mangler fast rapportformat.')
  }

  const penalty = blockers.length * 25 + warnings.length * 8
  const score = Math.max(0, 100 - penalty)
  return {
    isReady: blockers.length === 0,
    score,
    blockers,
    warnings,
  }
}

export function workflowHealthLabel(score: number): string {
  if (score >= 90) return 'Sterk'
  if (score >= 70) return 'God'
  if (score >= 50) return 'Ufullstendig'
  return 'Krever oppsett'
}

export function parseMultilineList(value: string): string[] | undefined {
  const items = value
    .split('\n')
    .map(item => item.trim().replace(/^[-*]\s*/, ''))
    .filter(Boolean)
  return items.length > 0 ? items : undefined
}

export function listToMultiline(value?: string[]): string {
  return value?.join('\n') ?? ''
}
